import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AlertTriangle, Loader2 } from "lucide-react"
import { formatInTz, isPolicyDaytime, wallTimeToIso } from "../lib/time"

export interface BookCallChoice {
  at?: string
  usePreference?: boolean
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  patientName: string
  /** YYYY-MM-DD / HH:MM in Central, to prefill. */
  initialDate?: string
  initialTime?: string | null
  /** Current pending call (ISO), if any; booking moves it. */
  pendingCallAt?: string | null
  /** Plain-English stored preference; enables "use the patient's own time". */
  preferenceSummary?: string | null
  onConfirm: (choice: BookCallChoice) => Promise<void>
}

function todayCentral(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date())
  return parts // en-CA formats as YYYY-MM-DD
}

export function BookCallDialog({
  open,
  onOpenChange,
  patientName,
  initialDate,
  initialTime,
  pendingCallAt,
  preferenceSummary,
  onConfirm,
}: Props) {
  const [date, setDate] = useState("")
  const [time, setTime] = useState("")
  const [usePref, setUsePref] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setDate(initialDate || todayCentral())
    setTime(initialTime || "10:00")
    setUsePref(false)
  }, [open, initialDate, initialTime])

  const at = date && time ? wallTimeToIso(date, time) : null
  const outside = !usePref && date && time ? !isPolicyDaytime(date, time) : false
  const canSubmit = usePref || (!!at && new Date(at).getTime() > Date.now() + 10 * 60 * 1000)

  const submit = async () => {
    setSubmitting(true)
    try {
      await onConfirm(usePref ? { usePreference: true } : { at: at! })
      onOpenChange(false)
    } catch {
      // The caller already told the user why; keep the dialog open to retry.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Book a call with {patientName}</DialogTitle>
          <DialogDescription>Times are Central time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {preferenceSummary && (
            <label className="flex items-start gap-2">
              <Checkbox checked={usePref} onCheckedChange={(c) => setUsePref(c === true)} className="mt-0.5" />
              <span>
                Use the time the patient asked for: <span className="font-medium">{preferenceSummary}</span>
              </span>
            </label>
          )}
          {!usePref && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="book-date">Date</Label>
                <Input id="book-date" type="date" min={todayCentral()} value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="book-time">Time (Central)</Label>
                <Input id="book-time" type="time" step={900} value={time} onChange={(e) => setTime(e.target.value)} />
              </div>
            </div>
          )}
          {outside && (
            <p className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-2 text-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              This is outside weekdays 8:00 AM to 7:00 PM Central. Only a time the patient chose can be
              outside that window, so a staff-picked time there will be refused.{" "}
              {preferenceSummary
                ? 'If the patient asked for it, tick "Use the time the patient asked for" above.'
                : "If the patient asked for it, save it in their contact preference with their own words first."}
            </p>
          )}
          <div className="rounded-md bg-muted/50 p-3 space-y-1">
            <p className="font-medium">What will happen</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                One call to {patientName}{" "}
                {usePref ? "at the time they asked for" : at ? <>on <strong>{formatInTz(at)}</strong></> : "at the chosen time"}.
              </li>
              {pendingCallAt && (
                <li>
                  Their call booked for {formatInTz(pendingCallAt)} is moved to this time. They will not get
                  two calls.
                </li>
              )}
              <li>
                One reminder text goes out before the call, on a weekday between 8:00 AM and 7:00 PM Central.{" "}
                {usePref
                  ? "Because the patient chose this time, it names the day and the exact time (for example \"tomorrow at 6:00 PM\")."
                  : "It names the day only (for example \"on Thursday\"), not the time."}{" "}
                No other text is sent.
              </li>
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit || submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : pendingCallAt ? "Move call" : "Book call"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
